import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { getAlerts, markAlert } from 'services/alerts.service';
import { useHistory } from 'react-router-dom';
import { useStore, useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import EventContext from 'eventBus/EventContext';
import eventBusTypes from 'eventBus/eventBusTypes';
import { setUnreadAlertAmountActionCreator } from 'plugins/store/reducers/common.reducer';
import layoutNames from 'utils/enums/layout-names.enum';

import SnackMessage from 'components/Snackbar/SnackMessage';

import styles from 'assets/jss/material-dashboard-pro-react/components/adminNavbarLinksStyle';

const snackMessage = (key, message) =>
	<SnackMessage
		id={key}
		message={message}
		color="danger"
	/>

const localStyles = {
	markReadButton: {
		padding: '3px 5px 4px 6px',
		backgroundColor: '#ffa40b',
		marginLeft: 5,
		margin: '0 0 0 5px',
		height: 20,
		fontSize: 11,
		fontWeight: 300,
		letterSpacing: 0,
		lineHeight: '24px',
		textTransform: 'initial',
		'&:hover, &:active, &:focus': {
			backgroundColor: '#ffa40b',
		}
	},
	markReadButtonMarked: {
		backgroundColor: '#999',
		'&:hover, &:active, &:focus': {
			backgroundColor: '#999',
		}
	},
	menuItem: {
		cursor: 'pointer',
		position: 'relative',
		padding: '15px 20px 15px 25px',
		borderBottom: '1px solid #ccc',
		fontSize: 13,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		width: 270,
		'&:hover, &:active, &:focus': {
			backgroundColor: '#eee',
		},
	},
	menuItemText: {
		maxHeight: 40,
		lineClamp: 2,
		display: '-webkit-box',
		overflow: 'hidden',
		lineHeight: '16px',
		letterSpacing: 0,
		fontWeight: 400,
		'-webkit-box-orient': 'vertical',
	},
	viewAll: {
		padding: '14px 0 15px 25px',
		fontSize: 13,
		lineHeight: '16px',
		color: '#333333',
		letterSpacing: 0,
	},
	loadingIndicatorWrapper: {
		zIndex: 10,
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.1)',
	},
	loadingIndicator: {
		position: 'absolute',
		left: '50%',
		top: '50%',
		transform: 'translate(-50%, -50%)',
	},
};

const useStyles = makeStyles(styles);
const useLocalStyles = makeStyles(localStyles);

const UPDATE_INTERVAL = 30000;
let timerId;
let mounted = false;

export default () => {
	const [openAlert, setOpenAlert] = useState(null);
	const [alerts, setAlerts] = useState([]);
	const [loading, setLoading] = useState(false);
	const classes = { ...useStyles(), ...useLocalStyles() };
	const history = useHistory();
	const userId = useSelector(state => state.auth.auth?.id);
	const { unreadAlertAmount } = useSelector(state => state.common);
	const store = useStore();
	const dispatch = useDispatch();
	const EventBus = useContext(EventContext);
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		mounted = true;
		getAlertsData();
		const callBack = EventBus.addEventListener(
			eventBusTypes.MARK_ALERT,
			({ id, isRead, fromAlertPage }) => {
				if (isRead === false && fromAlertPage) {
					getAlertsData();
				}
				changeStateAfterMark(id, isRead);
			},
		);
		return () => {
			mounted = false;
			clearTimeout(timerId);
			EventBus.removeEventListener(eventBusTypes.MARK_ALERT, callBack);
		};
	}, []);

	const getAlertsData = async () => {
		try {
			setLoading(true);
			const { data, dataLength } = await getAlerts({
				pageIndex: 0,
				pageSize: 5,
				isRead: false,
				userId,
			});
			if (mounted) {
				setAlerts(data);
				dispatch(setUnreadAlertAmountActionCreator(dataLength));

				clearTimeout(timerId);

				timerId = setTimeout(() => {
					getAlertsData();
				}, UPDATE_INTERVAL);
			}
		} catch (error) {
			console.log(error);
		}
		mounted && setLoading(false);
	};

	const closeAlert = () => {
		if (openAlert) {
			getAlertsData();
		}
		setOpenAlert(null);
	}

	const handleClickAlert = event => {
		if (openAlert && openAlert.contains(event.target)) {
			closeAlert();
		} else {
			setOpenAlert(event.currentTarget);
		}
	};

	const handleCloseAlert = () => {
		closeAlert();
	};

	const setNewStatusToItem = (id, isRead) => {
		setAlerts((items) => items.map((item) => {
			if (item.id === id) {
				item.isRead = isRead;
			}
			return item;
		}));
	};

	const setAlertLoadingStatus = (id, isLoading) => {
		setAlerts((items) => items.map((item) => {
			if (item.id === id) {
				item.isLoading = isLoading;
			}
			return item;
		}));
	}

	const markAsRead = async (event, { id, isRead, isLoading }) => {
		event.stopPropagation();
		if (isLoading) return;
		setAlertLoadingStatus(id, true);
		const newIsReadState = !isRead;
		try {
			await markAlert(id, newIsReadState);
		} catch (error) {
			showErrorMessage(error.resolvedErrorMessage);
		}
		setAlertLoadingStatus(id, false);
		EventBus.dispatchEvent({
			type: eventBusTypes.MARK_ALERT,
			payload: { id, isRead: newIsReadState },
		});
	};

	const changeStateAfterMark = (id, newIsReadState) => {
		const { common: { unreadAlertAmount } } = store.getState();
		const newUnreadAlertAmountValue = unreadAlertAmount + (newIsReadState ? -1 : 1);

		dispatch(setUnreadAlertAmountActionCreator(newUnreadAlertAmountValue));
		setNewStatusToItem(id, newIsReadState);
	};

	const toAlertPage = () => {
		closeAlert();
		history.push(`/${layoutNames.portal}/alerts`);
	};

	const getMarkButtonClasses = (isRead) => {
		return isRead
			? `${classes.markReadButton} ${classes.markReadButtonMarked}`
			: classes.markReadButton;
	};

	const showErrorMessage = (errorMessage) => {
		enqueueSnackbar(
			errorMessage,
			{ content: snackMessage },
		);
	};

	return {
		alerts,
		openAlert,
		alertsCount: unreadAlertAmount,
		classes,
		loading,
		handleClickAlert,
		toAlertPage,
		markAsRead,
		handleCloseAlert,
		getMarkButtonClasses,
	};
};
